import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, DataTable, EmptyState, type ColumnConfig } from '@/components/ui';
import { appTileLabel } from './coverageHelpers';
import type { MatrixAppColumn } from './coverageMatrixHelpers';
import type { MatrixCell } from './types';
import type { RoleAxisBoardVM } from './useRoleAxisBoard';
import shared from './coverageShared.module.css';
import styles from './RoleAxisLinksCard.module.css';

interface AppLinkRow {
    appCode: string;
    appName: string;
    ready: boolean;
    cell: MatrixCell;
}

/**
 * EAA-1817: the **Theo vai trò** detail pane — for the standard role selected
 * in the rail, one row per application showing what that role becomes there.
 *
 * READ-AND-JUMP, NOT A SECOND EDITOR. Every row's "Sửa" opens the per-app board
 * with this app selected and this role's row focused, which is where the chip
 * editor lives. That is deliberate: a picker on each row here would need one
 * `GET /admin/app-roles?application=…` per visible app (the catalog is per-app,
 * so there is no single option list this table could share), and it would put a
 * second editable surface over the same `RoleMap` rows — two editors for one
 * concept is the drift EAA-1841 merged these routes to stop. The design puts
 * editing on frame 55 and this view is not frame 55.
 */
export function RoleAxisLinksCard({
    vm,
    columns,
    onOpenCell,
}: {
    vm: RoleAxisBoardVM;
    /** Every app column, in registry order — matches `MatrixRoleRow.cells`. */
    columns: MatrixAppColumn[];
    onOpenCell?: (cell: MatrixCell) => void;
}) {
    const { t } = useTranslation();
    const row = vm.selectedRow;

    const rows = useMemo<AppLinkRow[]>(() => {
        if (!row) return [];
        return columns.map((column, index) => ({
            appCode: column.code,
            appName: column.name,
            ready: column.ready,
            cell: row.cells[index],
        }));
    }, [row, columns]);

    const tableColumns = useMemo<ColumnConfig<AppLinkRow>[]>(
        () => [
            {
                key: 'app',
                title: t('console.roleCoverage.roleAxisCard.columnApp'),
                render: (_value, item) => (
                    <span className={styles.roleAppCell}>
                        <span className={shared.appTile} aria-hidden="true">
                            {appTileLabel(item.appCode)}
                        </span>
                        <span className={styles.roleAppCellText}>
                            <span className={styles.roleAppCellName}>{item.appName}</span>
                            <code className={styles.roleAppCellCode}>{item.appCode}</code>
                        </span>
                    </span>
                ),
            },
            {
                key: 'appRoles',
                title: t('console.roleCoverage.roleAxisCard.columnAppRoles'),
                render: (_value, item) => {
                    if (!item.ready) {
                        return (
                            <span className="portal-admin-hint">
                                {t('console.roleCoverage.matrixView.appNotReady')}
                            </span>
                        );
                    }
                    if (item.cell.roles.length === 0) {
                        return (
                            <span className="portal-console-chips">
                                <Chip tone="unmapped">
                                    {t('console.roleCoverage.roleAxisCard.notLinked')}
                                </Chip>
                            </span>
                        );
                    }
                    return (
                        <span className="portal-console-chips">
                            {item.cell.roles.map((appRole) => (
                                <Chip
                                    key={appRole}
                                    tone={item.cell.state === 'paused' ? 'stale' : 'mapped'}
                                >
                                    {appRole}
                                </Chip>
                            ))}
                        </span>
                    );
                },
            },
            {
                key: 'state',
                title: t('console.roleCoverage.roleAxisCard.columnState'),
                /* Frame 55's own "Trạng thái" vocabulary, not `cellState`'s.
                   `cellState` names what a MATRIX CELL is ("gộp nhiều vai trò")
                   because there the state is all the cell has room to say; a
                   table row already shows its chips, so what it owes the reader
                   is the operational answer — is this link running, is it
                   paused, is there none. Same three words as every other status
                   column in the console. */
                render: (_value, item) => {
                    if (!item.ready) {
                        return t('console.roleCoverage.matrixView.appNotReady');
                    }
                    if (item.cell.state === 'paused') {
                        return t('console.roleCoverage.roleAxisCard.statePaused');
                    }
                    if (item.cell.state === 'none') {
                        return t('console.roleCoverage.roleAxisCard.stateUncovered');
                    }
                    return t('console.roleCoverage.roleAxisCard.stateActive');
                },
            },
        ],
        [t]
    );

    if (!row) {
        return (
            <EmptyState
                title={t('console.roleCoverage.roleAxisCard.emptyTitle')}
                description={t('console.roleCoverage.roleAxisCard.emptyDescription')}
            />
        );
    }

    return (
        <section
            className={`hz-card ${styles.roleAxisCard}`}
            aria-label={t('console.roleCoverage.roleAxisCard.ariaLabel')}
        >
            {/* EAA-1846: `data-testid` replaces the old `.rc-matrix-card-head` e2e hook —
                the class name is hashed by CSS Modules now (see role-map.spec.ts). */}
            <div className={shared.cardHead} data-testid="rc-matrix-card-head">
                <h2 className={shared.cardTitle}>
                    {t('console.roleCoverage.roleAxisCard.title', {
                        roleName: row.roleName,
                        covered: row.coveredCount,
                        total: row.readyAppCount,
                    })}
                </h2>
                <p className={shared.cardSubtitle}>
                    <code>{row.roleKey}</code>
                </p>
            </div>
            {/* The "Sửa" control goes through `DataTable`'s own `actions` slot
                rather than a column with an empty title: that slot is a fixed
                right-hand gutter, so the button cannot widen the table into a
                horizontal scrollbar the way a real column does. */}
            <DataTable<AppLinkRow>
                columns={tableColumns}
                data={rows}
                rowKey="appCode"
                pagination={false}
                actions={(item) =>
                    item.ready && onOpenCell ? (
                        <Button size="small" onClick={() => onOpenCell(item.cell)}>
                            {t('console.roleCoverage.roleAxisCard.editInApp')}
                        </Button>
                    ) : null
                }
                actionsWidth={92}
            />
        </section>
    );
}

export default RoleAxisLinksCard;
